import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Radio, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { KCard } from "@/components/KCard";
import { cn } from "@/lib/utils";
import type { ChapterDetails, LiveSessionDetails } from "@/api/lms/courses";

export type CalendarEventItem = {
  id: string;
  title: string;
  start: Date;
  end: Date;
  /** Student lesson deep link */
  href?: string;
  /** Course live session meeting URL */
  meetingLink?: string | null;
  kind: "live-lesson" | "live-session";
};

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function buildEvents(courseId: string, chapters: ChapterDetails[], sessions: LiveSessionDetails[]): CalendarEventItem[] {
  const out: CalendarEventItem[] = [];

  for (const ch of chapters) {
    for (const lesson of ch.lessons ?? []) {
      if (lesson.type !== "LIVE") continue;
      if (!lesson.liveStartAt || !lesson.liveEndAt) continue;
      const start = new Date(lesson.liveStartAt);
      const end = new Date(lesson.liveEndAt);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
      out.push({
        id: `lesson-${lesson.id}`,
        title: lesson.title,
        start,
        end,
        href: `/lesson?courseId=${encodeURIComponent(courseId)}&chapterId=${encodeURIComponent(ch.id)}&lessonId=${encodeURIComponent(lesson.id)}`,
        kind: "live-lesson",
      });
    }
  }

  for (const s of sessions) {
    const start = new Date(s.startTime);
    const end = new Date(s.endTime);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
    out.push({
      id: `session-${s.id}`,
      title: s.title,
      start,
      end,
      meetingLink: s.meetingLink ?? null,
      kind: "live-session",
    });
  }

  return out.sort((a, b) => a.start.getTime() - b.start.getTime());
}

/** Events that overlap a local calendar day [00:00, next day 00:00). */
function eventsOnLocalDay(events: CalendarEventItem[], y: number, m: number, day: number): CalendarEventItem[] {
  const dayStart = new Date(y, m, day, 0, 0, 0, 0);
  const dayEnd = new Date(y, m, day + 1, 0, 0, 0, 0);
  return events
    .filter((e) => e.start < dayEnd && e.end > dayStart)
    .sort((a, b) => a.start.getTime() - b.start.getTime());
}

const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function StudentCourseCalendar({
  courseId,
  chapters,
  liveSessions,
}: {
  courseId: string;
  chapters: ChapterDetails[];
  liveSessions: LiveSessionDetails[];
}) {
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  /** Day of month (1–31) in `cursor`’s month; null = none selected. */
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  const events = useMemo(
    () => buildEvents(courseId, chapters, liveSessions),
    [courseId, chapters, liveSessions],
  );

  useEffect(() => {
    setSelectedDay(null);
  }, [cursor.getFullYear(), cursor.getMonth()]);

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const monthLabel = cursor.toLocaleString(undefined, { month: "long", year: "numeric" });

  const eventsInMonth = useMemo(
    () => events.filter((e) => e.start <= monthEnd && e.end >= monthStart),
    [events, monthStart, monthEnd],
  );

  const firstDow = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const today = new Date();

  const upcoming = useMemo(() => {
    const now = new Date();
    return events.filter((e) => e.end >= now).slice(0, 8);
  }, [events]);

  function dayHasEvent(day: number): boolean {
    const dayDate = new Date(cursor.getFullYear(), cursor.getMonth(), day);
    const next = new Date(cursor.getFullYear(), cursor.getMonth(), day + 1);
    return eventsInMonth.some((e) => e.start < next && e.end >= dayDate);
  }

  const selectedDayEvents = useMemo(() => {
    if (selectedDay === null) return [];
    return eventsOnLocalDay(events, cursor.getFullYear(), cursor.getMonth(), selectedDay);
  }, [events, selectedDay, cursor]);

  const selectedDateLabel =
    selectedDay !== null
      ? new Date(cursor.getFullYear(), cursor.getMonth(), selectedDay).toLocaleDateString(undefined, {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : null;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <KCard className="p-5 md:p-6">
        <div className="mb-4 flex items-center justify-between gap-2">
          <h3 className="text-lg font-bold text-foreground">Course calendar</h3>
          <div className="flex items-center gap-1">
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCursor((c) => addMonths(c, -1))}>
              <ChevronLeft size={18} />
            </Button>
            <span className="min-w-[140px] text-center text-sm font-semibold text-foreground">{monthLabel}</span>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCursor((c) => addMonths(c, 1))}>
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>

        <div className="mb-2 grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {weekdayLabels.map((w) => (
            <div key={w}>{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, i) => {
            if (day === null) return <div key={`e-${i}`} className="aspect-square" />;
            const isToday = sameDay(today, new Date(cursor.getFullYear(), cursor.getMonth(), day));
            const has = dayHasEvent(day);
            const isSelected = selectedDay === day;
            return (
              <button
                key={day}
                type="button"
                onClick={() => setSelectedDay((prev) => (prev === day ? null : day))}
                className={cn(
                  "flex aspect-square flex-col items-center justify-center rounded-lg text-sm transition-colors",
                  "hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isToday && "bg-primary/15 font-bold text-primary",
                  !isToday && "text-foreground",
                  isSelected && "ring-2 ring-primary bg-primary/10",
                )}
              >
                <span>{day}</span>
                {has && (
                  <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-primary" aria-hidden title="Has scheduled class" />
                )}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Click a date to see classes that day. Dots mark days with a live lesson or session.
        </p>

        {selectedDay !== null && selectedDateLabel && (
          <div className="mt-5 border-t border-border pt-4">
            <h4 className="text-sm font-bold text-foreground mb-3">{selectedDateLabel}</h4>
            {selectedDayEvents.length === 0 ? (
              <p className="text-sm text-muted-foreground">No classes scheduled on this date.</p>
            ) : (
              <ul className="space-y-2">
                {selectedDayEvents.map((e) => (
                  <li key={e.id}>
                    {e.href ? (
                      <Link
                        to={e.href}
                        className="block rounded-xl border border-border/60 bg-background/80 p-3 transition-colors hover:bg-secondary/60"
                      >
                        <div className="flex items-start gap-2">
                          {e.kind === "live-lesson" ? (
                            <Video size={16} className="mt-0.5 shrink-0 text-primary" />
                          ) : (
                            <Radio size={16} className="mt-0.5 shrink-0 text-success" />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-foreground text-sm">{e.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {e.start.toLocaleTimeString(undefined, { timeStyle: "short" })} –{" "}
                              {e.end.toLocaleTimeString(undefined, { timeStyle: "short" })}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ) : (
                      <div className="rounded-xl border border-border/60 bg-background/80 p-3">
                        <div className="flex items-start gap-2">
                          <Radio size={16} className="mt-0.5 shrink-0 text-success" />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-foreground text-sm">{e.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {e.start.toLocaleTimeString(undefined, { timeStyle: "short" })} –{" "}
                              {e.end.toLocaleTimeString(undefined, { timeStyle: "short" })}
                            </p>
                            {e.meetingLink ? (
                              <a
                                href={e.meetingLink}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-2 inline-flex text-xs font-medium text-primary underline"
                              >
                                Open meeting link
                              </a>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </KCard>

      <KCard className="p-5 md:p-6">
        <h3 className="mb-4 text-lg font-bold text-foreground">Upcoming live</h3>
        {upcoming.length === 0 ? (
          <p className="text-sm text-muted-foreground">No upcoming live events scheduled.</p>
        ) : (
          <ul className="space-y-3">
            {upcoming.map((e) => (
              <li key={e.id}>
                {e.href ? (
                  <Link to={e.href} className="block rounded-xl border border-border/60 bg-background/50 p-3 transition-colors hover:bg-secondary/50">
                    <div className="flex items-start gap-2">
                      {e.kind === "live-lesson" ? (
                        <Video size={16} className="mt-0.5 shrink-0 text-primary" />
                      ) : (
                        <Radio size={16} className="mt-0.5 shrink-0 text-success" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-foreground">{e.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {e.start.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })} –{" "}
                          {e.end.toLocaleTimeString(undefined, { timeStyle: "short" })}
                        </p>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="rounded-xl border border-border/60 bg-background/50 p-3">
                    <div className="flex items-start gap-2">
                      <Radio size={16} className="mt-0.5 shrink-0 text-success" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-foreground">{e.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {e.start.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })} –{" "}
                          {e.end.toLocaleTimeString(undefined, { timeStyle: "short" })}
                        </p>
                        {e.meetingLink ? (
                          <a
                            href={e.meetingLink}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 inline-block text-xs font-medium text-primary underline"
                          >
                            Open meeting link
                          </a>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </KCard>
    </div>
  );
}
